import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Copy, Share2, PartyPopper, AlertTriangle, Clock3 } from "lucide-react";
import Seo from "../components/Seo";
import { ZORBA, whatsappLink } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoucherSuccess() {
  const [params] = useSearchParams();
  const code = params.get("code") || "";
  const pending = params.get("pending") === "1";
  const [voucher, setVoucher] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!code) return;
    axios
      .get(`${API}/vouchers/by-code/${encodeURIComponent(code)}`)
      .then((r) => setVoucher(r.data.voucher))
      .catch((err) => {
        if (pending) {
          // Expected — voucher is still PENDING because Razorpay keys were absent.
          setVoucher({ voucher_code: code });
        } else {
          setError(err?.response?.data?.detail || "Could not load this voucher.");
        }
      });
  }, [code, pending]);

  function copyCode() {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    }
  }

  const shareMessage = voucher
    ? `Hi ${voucher.recipient_name || "there"}! I've gifted you a wellness experience at ${ZORBA.name}. ` +
      `Your voucher code: ${voucher.voucher_code || code}. ` +
      `Service: ${voucher.service_name || ""} ${voucher.duration ? `(${voucher.duration})` : ""}. ` +
      `Call or WhatsApp the spa at +91 85228 16969 to book your slot — valid till ${voucher.expiry_date?.slice(0, 10) || "6 months"}.`
    : "";

  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;

  return (
    <>
      <Seo
        title={pending ? "Voucher ready" : "Voucher purchased"}
        description="Your Zorba International Spa gift voucher is ready to share."
        path="/gift-vouchers/success/"
      />
      <section className="section-pad narrow voucher-success" data-testid="voucher-success">
        {pending ? (
          <div className="success-head pending">
            <AlertTriangle size={24} />
            <p className="eyebrow">Payment setup in progress</p>
            <h1>We've reserved your voucher code.</h1>
            <p className="muted">
              Razorpay isn't fully configured on this server yet, so the voucher is held as{" "}
              <strong>pending</strong>. Share this code with the spa over WhatsApp — they'll confirm payment manually and
              activate it.
            </p>
          </div>
        ) : (
          <div className="success-head">
            <PartyPopper size={24} />
            <p className="eyebrow">Thank you</p>
            <h1>The voucher is ready to share.</h1>
            <p className="muted">
              {voucher?.recipient_name ? `${voucher.recipient_name} is going to love this.` : "A lovely gift is on its way."}{" "}
              Save the code below — you can also share it directly over WhatsApp.
            </p>
          </div>
        )}

        {error && <p className="form-error">{error}</p>}

        <div className="voucher-card" data-testid="voucher-card">
          <p className="eyebrow">Zorba Gift Voucher</p>
          <h2>{voucher?.service_name || "Zorba experience"}</h2>
          {voucher?.duration && <p className="muted-small">{voucher.duration}</p>}
          <div className="voucher-code-row">
            <code data-testid="voucher-code">{voucher?.voucher_code || code}</code>
            <button type="button" onClick={copyCode} className="btn btn-secondary" data-testid="voucher-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {voucher?.personal_message && (
            <blockquote className="voucher-message">"{voucher.personal_message}"</blockquote>
          )}
          <div className="voucher-fine">
            <span><Clock3 size={13} /> Valid for 6 months from issue</span>
            <span>Redeem in-person at {ZORBA.name}, Chandrasekharpur</span>
          </div>
        </div>

        <div className="actions-row" style={{ marginTop: 28 }}>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="voucher-share-whatsapp">
            <Share2 size={14} /> Share on WhatsApp
          </a>
          <a
            href={whatsappLink(
              `Hello Zorba, I just purchased a gift voucher (code ${voucher?.voucher_code || code}). ` +
                (pending ? "Could you please confirm payment and activate it?" : "Could you help the recipient book a slot?"),
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            data-testid="voucher-contact-spa"
          >
            Message the spa
          </a>
          <Link to="/gift-vouchers/" className="btn btn-secondary">Buy another voucher</Link>
        </div>

        <p className="muted-small" style={{ marginTop: 30 }}>
          Lost the code? Email <a href="mailto:hello@spanearmebhubaneswar.com">hello@spanearmebhubaneswar.com</a> with
          the recipient's mobile number and we'll resend it.
        </p>
      </section>
    </>
  );
}
